import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { verificationAPI } from '@/shared/lib/api';
import { verificationCodeSchema } from '@/shared/lib/validations';
import { useTimer } from '@/shared/hooks/useTimer';
import { formatTimer, removePhoneMask } from '@/shared/lib/utils';
import { toast } from '@/shared/lib/toast';
import { Loader2, RefreshCw, CheckCircle2 } from 'lucide-react';
import type { VerifyCodeResponse, SendVerificationCodeResponse } from '@/shared/types';

interface VerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  phone: string;
  expiresAt: string;
  onVerified: () => void;
}

type VerificationFormData = z.infer<typeof verificationCodeSchema>;

const TIMER_DURATION = 60; // 1 minuto em segundos

export function VerificationModal({
  isOpen,
  onClose,
  phone,
  expiresAt: initialExpiresAt,
  onVerified,
}: VerificationModalProps) {
  const [expiresAt, setExpiresAt] = useState(initialExpiresAt);
  const { timeLeft, isExpired, start, reset } = useTimer();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset: resetForm,
  } = useForm<VerificationFormData>({
    resolver: zodResolver(verificationCodeSchema),
  });

  // Inicializar timer quando modal abre ou novo código é enviado
  useEffect(() => {
    if (isOpen && expiresAt) {
      const expiryTime = new Date(expiresAt).getTime();
      const now = Date.now();
      const secondsLeft = Math.max(0, Math.floor((expiryTime - now) / 1000));
      start(secondsLeft);
    }
  }, [isOpen, expiresAt, start]);

  // Limpar quando modal fecha
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  // Mutation para verificar código
  const verifyMutation = useMutation({
    mutationFn: async (data: VerificationFormData) => {
      const response = await verificationAPI.verifyCode({
        phone: removePhoneMask(phone),
        code: data.code,
      });
      // response.data = { data: {...}, timestamp }
      // Extrair os dados reais
      return response.data.data as VerifyCodeResponse;
    },
    onSuccess: (data) => {
      if (data.verified) {
        toast.success(data.message || 'Telefone verificado com sucesso!');
        onVerified();
        onClose();
      } else {
        setError('code', {
          type: 'manual',
          message: data.message || 'Código inválido. Verifique e tente novamente.',
        });
      }
    },
    onError: () => {
      setError('code', {
        type: 'manual',
        message: 'Erro ao verificar código. Tente novamente.',
      });
    },
  });

  // Mutation para reenviar código
  const resendMutation = useMutation({
    mutationFn: async () => {
      const response = await verificationAPI.sendCode({
        phone: removePhoneMask(phone),
      });
      // response.data = { data: {...}, timestamp }
      // Extrair os dados reais
      return response.data.data as SendVerificationCodeResponse;
    },
    onSuccess: (data) => {
      setExpiresAt(data.expiresAt);
      start(TIMER_DURATION);
      resetForm();
      toast.success('Novo código enviado via WhatsApp!');
    },
    onError: () => {
      toast.error('Erro ao reenviar código. Tente novamente.');
    },
  });

  const onSubmit = (data: VerificationFormData) => {
    if (isExpired) {
      setError('code', {
        type: 'manual',
        message: 'Código expirado. Solicite um novo código.',
      });
      return;
    }
    verifyMutation.mutate(data);
  };

  const handleResend = () => {
    resendMutation.mutate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="p-0">
        <DialogHeader>
          <DialogTitle>Verificação do Telefone</DialogTitle>
          <DialogClose onClick={onClose} />
        </DialogHeader>

        <form key={expiresAt} onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    Código enviado via WhatsApp
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Enviamos um código de 6 dígitos para o número{' '}
                    <span className="font-semibold">{phone}</span>
                  </p>
                </div>
              </div>
            </div>

            <Input
              label="Código de Verificação"
              type="text"
              placeholder="000000"
              maxLength={6}
              error={errors.code?.message}
              {...register('code')}
              autoFocus
              disabled={verifyMutation.isPending || isExpired}
            />

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                {!isExpired ? (
                  <>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Código expira em:
                    </span>
                    <span className="text-sm font-mono font-semibold text-gray-900 dark:text-gray-100">
                      {formatTimer(timeLeft)}
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-red-600 dark:text-red-400 font-medium">
                    Código expirado
                  </span>
                )}
              </div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleResend}
                disabled={resendMutation.isPending || (!isExpired && timeLeft > 0)}
              >
                {resendMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reenviar código
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={verifyMutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={verifyMutation.isPending || isExpired}>
              {verifyMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verificando...
                </>
              ) : (
                'Verificar Código'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
